import { motion } from "framer-motion";
import { AlertTriangle, Compass, Zap, Shield, Lightbulb, TrendingUp, Clock, Search, Layers, Settings, BarChart3 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Section, SectionHeader } from "./shared";
import type { SolutionData } from "@/data/solutionsData";

const FALLBACK_ICONS: LucideIcon[] = [Compass, Zap, Shield, Lightbulb, TrendingUp, Clock, Search, Layers, Settings, BarChart3];

const getUniqueIcons = (challenges: SolutionData["challenges"]) => {
  const used = new Set(challenges.filter(c => c.icon).map(c => c.icon));
  const available = FALLBACK_ICONS.filter(ic => !used.has(ic));
  let idx = 0;
  return challenges.map(c => c.icon || available[idx++ % available.length] || Compass);
};

interface Props {
  solution: SolutionData;
}

const SolutionChallenges = ({ solution }: Props) => {
  const section = solution.challengesSection;
  if (!section || solution.challenges.length === 0) return null;

  const icons = getUniqueIcons(solution.challenges);

  return (
    <Section>
      <SectionHeader label={section.label || "Industry context"} title={section.title} icon={AlertTriangle} />
      {section.intro && (
        <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 leading-relaxed">{section.intro}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
        {solution.challenges.map((c, i) => {
          const Icon = icons[i];
          return (
            <motion.div
              key={i}
              className="p-5 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-md transition-all"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.06 }}
            >
              <Icon size={26} className="mb-3 text-primary" strokeWidth={1.5} />
              <h3 className="text-base font-semibold text-foreground mb-1.5">{c.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
            </motion.div>
          );
        })}
      </div>

      {section.callout && (
        <motion.div
          className="mt-10 max-w-3xl mx-auto rounded-2xl bg-primary text-primary-foreground px-7 py-6 shadow-lg shadow-primary/20"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-sm sm:text-base leading-relaxed">{section.callout}</p>
        </motion.div>
      )}
    </Section>
  );
};

export default SolutionChallenges;
